import type { VideoFilters } from "@/types/video";
import { toggleValue, yearOptionLabel } from "@/utils/video";

export interface FilterGroupConfig {
  key: "genres" | "regions" | "tags" | "status" | "platforms" | "yearRange";
  label: string;
  options: string[];
}

interface FilterPanelProps {
  groups: FilterGroupConfig[];
  filters: VideoFilters;
  yearOptions: string[];
  onChange: (filters: VideoFilters) => void;
  onReset: () => void;
}

export default function FilterPanel({ groups, filters, yearOptions, onChange, onReset }: FilterPanelProps) {
  const handleMulti = (key: FilterGroupConfig["key"], value: string) => {
    if (key === "yearRange") return;
    const current = filters[key];
    onChange({ ...filters, [key]: toggleValue(current, value) });
  };

  const handleYear = (value: string) => {
    onChange({ ...filters, yearRange: value });
  };

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.key}>
          <h3 className="mb-3 font-heading text-sm font-bold text-foreground-900">{group.label}</h3>
          {group.key === "yearRange" ? (
            <div className="flex flex-wrap gap-2">
              {yearOptions.map((option) => {
                const active = filters.yearRange === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleYear(option)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? "border-primary-600 bg-primary-600 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                    }`}
                  >
                    {yearOptionLabel(option)}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {group.options.map((option) => {
                const active = filters[group.key].includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleMulti(group.key, option)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? "border-secondary-600 bg-secondary-600 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400 hover:text-secondary-700"
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={onReset}
        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
      >
        <i className="ri-refresh-line" />
        重置筛选
      </button>
    </div>
  );
}